import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Key, MagnifyingGlass, ShieldCheck, At, Eye, EyeSlash, ArrowsClockwise, Warning, Trash, UserCirclePlus, Check, X } from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: "bg-primary/10 text-primary border-primary/20",
  manager: "bg-warning/10 text-warning border-warning/20",
  supervisor: "bg-accent-foreground/10 text-accent-foreground border-border",
  employee: "bg-muted text-muted-foreground border-border",
};

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let p = "";
  for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p + "@1";
}

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [pwd, setPwd] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("employee");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [pending, setPending] = useState([]);
  const [pendingBusy, setPendingBusy] = useState("");

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pending-users");
      setPending(data);
    } catch (e) { /* ignore */ }
  }, []);

  const approveUser = async (u) => {
    setPendingBusy(u.id);
    try {
      await api.post(`/admin/pending-users/${u.id}/approve`);
      toast.success(`${u.name} approved — they can now sign in`);
      loadPending();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPendingBusy("");
    }
  };

  const rejectUser = async (u) => {
    setPendingBusy(u.id);
    try {
      await api.delete(`/admin/pending-users/${u.id}`);
      toast.success(`${u.name}'s sign-up was rejected and removed`);
      loadPending();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPendingBusy("");
    }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset");
      const c = data.cleared || {};
      toast.success(`Data reset. Removed ${c.users || 0} users, ${c.payrolls || 0} payslips, ${c.leaves || 0} leaves.`);
      setResetOpen(false);
      setResetText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: { q: q || undefined } });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const openManage = (u) => {
    setTarget(u);
    setName(u.name || "");
    setRole(u.role);
    setPwd("");
    setShow(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (name.trim() && name.trim() !== target.name) updates.name = name.trim();
      if (role !== target.role) updates.role = role;
      if (Object.keys(updates).length) await api.put(`/employees/${target.id}`, updates);
      if (pwd.trim()) await api.put(`/employees/${target.id}/password`, { password: pwd.trim() });
      toast.success("Account updated");
      setTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-admin-page">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
          <Key weight="duotone" className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Administration</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">User Accounts</h1>
          <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-sm">
            <ShieldCheck className="h-4 w-4" /> Admin-only. Manage usernames, roles, and reset passwords.
          </p>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="users-search" placeholder="Search by username or name..." className="pl-9"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {pending.length > 0 && (
        <Card className="border-warning/40 bg-warning/5 overflow-hidden" data-testid="pending-approvals-card">
          <div className="p-5 border-b border-warning/20 flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-warning/15 text-warning flex items-center justify-center shrink-0">
              <UserCirclePlus weight="duotone" className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg leading-tight">Pending Approvals</h3>
              <p className="text-sm text-muted-foreground">New sign-ups can't access the portal until you approve them.</p>
            </div>
            <Badge variant="outline" className="ml-auto bg-warning/10 text-warning border-warning/30 font-semibold" data-testid="pending-count">
              {pending.length}
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-warning/5 hover:bg-warning/5">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Sign-in</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((u) => (
                  <TableRow key={u.id} data-testid={`pending-row-${u.id}`} className="animate-fade-up">
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <At className="h-3.5 w-3.5" />{u.email}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm capitalize">{u.auth_provider === "google" ? "Google" : "Password"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" data-testid={`approve-user-${u.id}`} disabled={pendingBusy === u.id}
                          onClick={() => approveUser(u)} className="gap-1.5 bg-success hover:bg-success/90 text-white font-semibold">
                          <Check className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`reject-user-${u.id}`} disabled={pendingBusy === u.id}
                          onClick={() => rejectUser(u)} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
                          <X className="h-3.5 w-3.5" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Sign-in</TableHead>
                <TableHead className="text-right">Manage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No users found.</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="animate-fade-up">
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <At className="h-3.5 w-3.5" />{u.email}
                    </span>
                  </TableCell>
                  <TableCell><Badge variant="outline" className={`capitalize font-medium ${ROLE_BADGE[u.role]}`}>{u.role}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-sm capitalize">{u.auth_provider === "google" ? "Google" : "Password"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" data-testid={`manage-user-${u.id}`} onClick={() => openManage(u)} className="gap-1.5">
                      <Key className="h-3.5 w-3.5" /> Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md" data-testid="manage-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Manage account</DialogTitle>
            <DialogDescription>{target?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Username (email)</Label>
              <Input value={target?.email || ""} disabled data-testid="manage-username" />
            </div>
            <div className="space-y-1.5">
              <Label>Display name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="manage-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="manage-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Set / reset password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type={show ? "text" : "password"} value={pwd} placeholder="Leave blank to keep current"
                    onChange={(e) => setPwd(e.target.value)} data-testid="manage-password" className="pr-9" />
                  <button type="button" onClick={() => setShow(!show)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {show ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" data-testid="generate-password"
                  onClick={() => { setPwd(genPassword()); setShow(true); }} className="gap-1.5 shrink-0">
                  <ArrowsClockwise className="h-4 w-4" /> Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 characters. The user signs in with their email and this password.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-button" className="font-semibold">
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-6 border-destructive/40 bg-destructive/5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
            <Warning weight="duotone" className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">Danger zone — Reset data</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Permanently deletes all employees/managers, payslips, leave requests, 201 files, documents,
              notifications and audit logs. <span className="font-semibold text-foreground">Admin accounts are kept.</span> This cannot be undone.
            </p>
            <Button variant="destructive" className="mt-4 gap-1.5" data-testid="open-reset-button"
              onClick={() => { setResetText(""); setResetOpen(true); }}>
              <Trash className="h-4 w-4" /> Reset all data
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Reset all data?</DialogTitle>
            <DialogDescription>
              This removes every employee, payslip, leave request, 201 file, document, notification and
              audit log. Only admin accounts remain. This action is permanent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
            <Input value={resetText} onChange={(e) => setResetText(e.target.value)}
              placeholder="RESET" data-testid="reset-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={resetText !== "RESET" || resetting}
              onClick={doReset} data-testid="confirm-reset-button" className="gap-1.5">
              <Trash className="h-4 w-4" /> {resetting ? "Resetting..." : "Permanently reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
